import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import EmptyState from '@/components/molecules/EmptyState';
import { AllocationSummary } from '@/components/organisms/FishGroupForms';
import type { FishGroupPopulationInfo, OverviewCage } from '@/domain/fishGroups';

import { computeAllocationSummary, type WizardValidationError } from '../../operateWizardHelpers';
import type {
    OperateWizardAction,
    WizardDestination,
    WizardSource,
} from '../../operateWizardReducer';
import { ValidationBanner } from '../shared/ValidationBanner';

import { DestinationCard } from './DestinationCard';

import styles from '../../operateModals.module.scss';

interface Props {
    /** Available cages in the operation's location (active + inactive, non-historical). */
    locationCages: OverviewCage[];
    sources: WizardSource[];
    destinations: WizardDestination[];
    errors: WizardValidationError[];
    populationById: Map<number, FishGroupPopulationInfo>;
    dispatch: (action: OperateWizardAction) => void;
}

export default function DestinationsStep(props: Props) {
    const { locationCages, sources, destinations, errors, populationById, dispatch } = props;
    const { t } = useTranslation('fishGroups');

    const allocationSummary = computeAllocationSummary({
        sources,
        destinations,
        populationById,
    });

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>{t('actions.operate.destinations.heading')}</h4>
                <p className={styles.description}>
                    {t('actions.operate.destinations.description')}
                </p>
            </div>

            <AllocationSummary
                summary={allocationSummary}
                sources={sources}
                populationById={populationById}
                titleKey="actions.operate.allocation.combinedSourcesTitle"
            />

            {errors.length > 0 && <ValidationBanner errors={errors} />}

            {destinations.length === 0 ? (
                <EmptyState title={t('actions.operate.destinations.empty')} appearance="quiet" />
            ) : (
                <div className={styles.destinationsList}>
                    {destinations.map((dest, index) => (
                        <DestinationCard
                            key={dest.tempId}
                            index={index}
                            destination={dest}
                            sources={sources}
                            locationCages={locationCages}
                            dispatch={dispatch}
                        />
                    ))}
                </div>
            )}

            <Button
                variant="secondary"
                size="small"
                type="button"
                className={styles.addButton}
                onClick={() => dispatch({ type: 'addDestination' })}>
                {t('actions.operate.destinations.addButton')}
            </Button>
        </section>
    );
}
